/*
 * ************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *   Copyright 2021 Adobe Systems Incorporated
 *   All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by all applicable intellectual property
 * laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 * ************************************************************************
 */

import { call, put, select, take, takeLatest } from 'redux-saga/effects';

import { LOCATION_CHANGED, getRouteVar } from '@nebula/rover';
import { SESSION_LIST } from '../../casing/data/pages';
import { STATEFUL_NAVIGATION, navigateToSessionViewSelected } from '../../common/actions';
import { selectInitedStateful } from '../../common/sagas';
import { selectSessionRouteParam } from '../../common/selectors';
import * as T from '../actions';
import { sendNavigateTo } from '../data/prefabs';
import { getSelectedViewNamespace, selectFilterHash } from '../selectors';

export function* handleSelectView({ payload }) {
  const session = yield select(selectSessionRouteParam);

  yield put(navigateToSessionViewSelected({
    session,
    view: payload
  }));
}

export function* handleStatefulNavigation(payload) {
  yield put(sendNavigateTo.turnOffAction);

  yield call(handleSelectView, payload);

  yield take(LOCATION_CHANGED);

  yield put(sendNavigateTo.turnOnAction);
}

export function* handleRouteLocationChanged() {
  const page = yield select(getRouteVar('page'));
  if (page === SESSION_LIST) {
    yield put(T.closeSession());
    return;
  }

  const callNavigateTo = yield select(sendNavigateTo.isOn);

  if (callNavigateTo) {
    const view = yield select(getSelectedViewNamespace);
    if (view) {
      const stateful = yield call(selectInitedStateful);
      const hash = yield select(selectFilterHash);
      const path = hash ? `${view}#${hash}` : view;
      yield call([stateful, 'navigateTo'], path);
    }
  }
}

export function* watchSelectViewSaga() {
  yield takeLatest(T.SELECT_VIEW, handleSelectView);
}

export function* watchStatefulNavigationSaga() {
  yield takeLatest(STATEFUL_NAVIGATION, handleStatefulNavigation);
}

export function* watchRouteLocationChangedSaga() {
  yield takeLatest(LOCATION_CHANGED, handleRouteLocationChanged);
}
